import { Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { IDatePlace } from "src/app/models/location.model";
import { ProfileModel } from "src/app/models/profile.model";
import { SaleDetails, TourneeModel } from "src/app/models/tournee.model";
import { DataService } from "../data/data.service";
import { DateService } from "../date/date.service";
import { ProductService } from "../product/product.service";

@Injectable({
  providedIn: "root"
})
export class SaleService {

  deliveryTypesWithTimingAndPlace = ["market", "collectpoint", "ownshop","own-shop", "pickupstore","locker","vendingMachine","company","association"];
  deliveryTypesWithName = ["pickupstore"];
  deliveryTypesWithTiming = ["market", "collectpoint", "ownshop", "pickupstore","locker","vendingMachine","company","association"];

  statusRank = ['created','accepted','full','delivered','rejected'];

  statuses = {
    created: {name:'created',status:false, icon:'notifications-outline', color:'primary'},
    draft: {name:'draft',status:false, icon:'create-outline', color:'primary'},
    open: {name:'open',status:false, icon:'cart-outline', color:'secondary'},
    accepted: {name:'accepted',status:false, icon:'checkmark-outline', color:'secondary'},
    prepared: {name:'prepared',status:false, icon:'checkmark-done-outline', color:'secondary'},
    full: {name:'full',status:false,icon:'bag-handle-outline', color:'secondary'},
    closed: {name:'full',status:false,icon:'stop-circle-outline', color:'light'},
    delivered: {name:'delivered',status:false,icon:'bag-handle-outline', color:'light'},
    rejected: {name:'rejected',status:false,icon:'close-outline', color:'light'},
    cancelled: {name:'cancelled',status:false,icon:'close-outline', color:'light'}
  }

  statusesArray = Object.values(this.statuses);

  constructor(private dateService:DateService, private dataService: DataService, private productService: ProductService, private translateService:TranslateService) {}
 
  showMoment(deliveryType:string):boolean{
    return !this.deliveryTypesWithTimingAndPlace.includes(deliveryType);
  }

  showDeliveryTimingAndPlace(deliveryType:string):boolean{
    return this.deliveryTypesWithTimingAndPlace.includes(deliveryType);
  }

  showDeliveryPlaceName(deliveryType:string):boolean{
    return this.deliveryTypesWithName.includes(deliveryType);
  }

  setPrimaryDatePlace(details:SaleDetails):SaleDetails{
    if(details.datesPlaces && details.datesPlaces.length>0){
      const datePlace = details.datesPlaces[0];
      details.date = datePlace.date;
      details.geocoding = datePlace.geocoding;
      details.fromTimeHour = datePlace.fromTimeHour;
      details.toTimeHour = datePlace.toTimeHour;
      details.dates = details.datesPlaces;
    }
    return details;
  }

  enrichPrices(sale: TourneeModel){
    // if(sale.details.priceMode == 'VATExcluded' && sale.products && sale.products.references){
    if(sale.products && sale.products.references){
      const products = Object.entries(sale.products.references);
      for(const [key, product] of products){
        sale.products.references[key] = this.productService.addVATExcludedPrice(product, sale.details?.geocoding?.address?.countryCode || 'FRA')
      }
    }
    return sale;
  }

  getDatesPlaces(details:SaleDetails):IDatePlace[]{
    if(!details) return null
    if(details.datesPlaces && details.datesPlaces.length>0){
      return details.datesPlaces;
    }
    else if(details.dates && details.dates.length>0){
      for(const date of details.dates){  // RETRO compatibility with old sales
        if(!date.geocoding){
          date.geocoding = details.geocoding;
        }
      }
      details.dates.unshift({
        date: details.date,
        geocoding : details.geocoding,
        fromTimeHour : details.fromTimeHour,
        toTimeHour : details.toTimeHour
      })
      return details.dates;
    }
    else if (details.date && details.geocoding) {  // RETRO compatibility with old sales
      const datePlace = {
        date: details.date,
        geocoding : details.geocoding,
        fromTimeHour : details.fromTimeHour,
        toTimeHour : details.toTimeHour}
      return [datePlace]
    }
    else return null
  }

  convertSaleToDatePlaceSystem(item:TourneeModel){
    item.details.datesPlaces = this.getDatesPlaces(item.details);
    return item;
  }
  convertSalesToDatePlaceSystem(items:TourneeModel[]){
    for(const item of items){
      if(item.details) item.details.datesPlaces = this.getDatesPlaces(item.details);
    }
    return items;
  }

  async cleanDirtySales(items){
    for(const item of items){
      if(!item.details || !item.details.datesPlaces || item.details.datesPlaces == null){
        const result = await this.dataService.deleteTournee(item);
        console.log('clean', item.key, item.details?.name, result);
        items.pop(item);
      }
    }
    return items
  }

  
  public createDatePlacesTextForPDF(sale:TourneeModel){
    let content = '';
    if(sale.details.recurrence == 'weekly'){
      content += this.translateService.instant("common.EveryDay")+" ";
    }
    if(sale.details.datesPlaces){
      for(const datePlace of sale.details.datesPlaces){
        content += this.dateService.formatDatePlace(datePlace,false,true, true) + "\r"
      }
    }
    content += "\r";
    return content;
  }
  sortAndTransformToArrayByType(items: TourneeModel[], producer?: ProfileModel) {
    items.sort(this.sortTourneeByDate);
    let itemsByType = { week: {}, status: {}, all: [], place: {} };
    const itemsByTypeArrays = { week: [], status: [], all: [], place: [] };
    const dateService = this.dateService;
    const reducer = function (accumulator, currentValue) {
      if(!currentValue || !currentValue.details) return accumulator;
      const date = dateService.getFirstDateStr(currentValue);
      const week = dateService.getWeekString(date);
      const status = currentValue.status;
      if(producer) currentValue.producer = producer;
      const place = currentValue.details.geocoding.address.city;
      if (week.name in accumulator.week) {
        accumulator.week[week.name].array.push(currentValue);
      } else {
        accumulator.week[week.name] = { order: week.order, name: week.name, array: [currentValue] };
      }
      if (status in accumulator.status) {
        accumulator.status[status].array.push(currentValue);
      } else {
        accumulator.status[status] = { name: status, array: [currentValue] };
      }
      if (place in accumulator.place) {
        accumulator.place[place].array.push(currentValue);
      } else {
        accumulator.place[place] = { name: place, array: [currentValue] };
      }
      accumulator.all.push(currentValue);
      return accumulator;
    };
    itemsByType = items.reduce(reducer, itemsByType);
    itemsByTypeArrays.all = itemsByType.all;
    if(itemsByType.week[this.translateService.instant("date.Earlier")]){
      itemsByType.week[this.translateService.instant("date.Earlier")].array.sort((a: TourneeModel, b: TourneeModel) => {
        return b.details.date < a.details.date ? -1 : 1;
      })      
    }

    itemsByTypeArrays.week = Object.values(itemsByType.week).sort(function (a: any, b: any) {
      return a.order < b.order ? -1 : 1;
    });

    itemsByTypeArrays.status = Object.values(itemsByType.status).sort(function (a: any, b: any) {
      return a.name < b.name ? -1 : 1;
    });
    itemsByTypeArrays.place = Object.values(itemsByType.place).sort(function (a: any, b: any) {
      return a.name < b.name ? -1 : 1;
    });
    console.log(itemsByTypeArrays);
    return itemsByTypeArrays;
  }

  groupTourneeByMonthYear(tournees:TourneeModel[],accumulator){
    tournees = tournees.sort(this.sortTourneeByDate);
    for(const tournee of tournees){
      const monthYear = this.dateService.formatToMonthYearDate(this.dateService.getFirstDateStr(tournee),true);
      if(!accumulator){
        accumulator = {};
      }
      if (!accumulator[monthYear]) {
        accumulator[monthYear] = [];
      }
      accumulator[monthYear].push(tournee);
    };
  }

  sortTournee(a: TourneeModel, b: TourneeModel): number {
    return a.details.date < b.details.date ? -1 : 0;
  }

  sortTourneeByDayName(a: TourneeModel, b: TourneeModel): number {
    const dayA = new Date(a.details.date);
    const dayB = new Date(b.details.date);
    const hourA = dayA.getHours();
    const hourB = dayB.getHours();
    const moduloA = (dayA.getDay() + 6) % 6;
    const moduloB = (dayB.getDay() + 6) % 6;
    console.log(a.details.date, dayA, moduloA, b.details.date, dayB, moduloB);
    return moduloA < moduloB && hourA < hourB ? -1 : 0;
  }

  sortTourneeByDate(a: TourneeModel, b: TourneeModel): number {
    if(a.details && b.details && a.details.date && b.details.date){
      return b.details.date < a.details.date ? -1 : 1;
    }
    else if(!b.details || !b.details.date){
      console.log('missing date', b.key, b.details?.name, b.details?.date);
      return 1
    }
    else if(!a.details || !a.details.date){
      console.log('missing date', a.key, a.details?.name, a.details?.date);
     return -1 
    }
  }

  filterOpenAndFuture(tournee: TourneeModel): boolean {
    const now = new Date();
    const date = new Date(tournee.details.date);
    return tournee.status == "open" && date > now && tournee.details.visibility == 'public';
  }

  getPhotoURL(tournee: TourneeModel): string {
    if (tournee.details.photoURL) {
      return tournee.details.photoURL;
    } else if (!!tournee.producer && !!tournee.producer.details && !!tournee.producer.details.shortDetails.photoURL) {
      return tournee.producer.details.shortDetails.photoURL;
    } else if (!!tournee.owner && !!tournee.owner.details && !!tournee.owner.details.shortDetails.photoURL) {
      return tournee.owner.details.shortDetails.photoURL;
    } else if (tournee.details.format == "group") {
      return "assets/photos/market.jpg";
    } else {
      return "assets/photos/" + tournee.details.type + "-small.jpg";
    }
  }

  isMultiPlaces(tournee: TourneeModel): boolean{
    if(! tournee.details || !tournee.details.datesPlaces || tournee.details.datesPlaces.length == 0) return false
    for(const item of tournee.details.datesPlaces){
      if(item.geocoding && tournee.details.datesPlaces[0].geocoding && item.geocoding.locationID !== tournee.details.datesPlaces[0].geocoding.locationID){
        return true;
      }
    }
    return false
  }
  getPlacesSummaryLabel(tournee:TourneeModel){
    if(! tournee.details) {
      return ''
    }
    else if (tournee.details.geocoding && (!tournee.details.datesPlaces || tournee.details.datesPlaces.length == 0)) { 
      return tournee.details.geocoding.label}
    else if (tournee.details.datesPlaces){
      if(tournee.details.datesPlaces.length == 1){
        return tournee.details.datesPlaces[0].geocoding.label;
      }
      else{
        const dates = tournee.details.datesPlaces.sort(this.dateService.sortByDates);
        const cities = dates.map(date => date.geocoding.address.city);
        const citiesWithoutDuplicates = cities.filter((value,index) => cities.indexOf(value) === index);
        const lastItem = citiesWithoutDuplicates.pop();
        return citiesWithoutDuplicates.join(', ') + 
            (citiesWithoutDuplicates.length>=1?(' ' + this.translateService.instant('common.and')+ ' '):'' )
            + lastItem
      }

    }
  }
}
